import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import Layout from './components/Layout';
import { LoginPage, RegisterPage } from './pages/Auth';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import { ProjectListPage, ProjectDetailPage, AllVariationsPage, InstructionDayworksPage } from './pages/ProjectPages';
import InstructionForm from './pages/InstructionForm';
import InstructionDetail from './pages/InstructionDetail';
import DayworkForm from './pages/DayworkForm';
import DayworkDetail from './pages/DayworkDetail';
import VariationForm from './pages/VariationForm';
import VariationDetail from './pages/VariationDetail';
import { MCPortalDaywork, MCPortalVariation, MCPortalEwnCvi, MCPortalDashboard } from './pages/MCPortal';
import { C, S, formatDate } from './styles';
import { supabase } from './supabase';

function ProjectSelectorPage({ navigate }) {
  const { profile } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile) return;
    supabase
      .from('project_users')
      .select('project_id, projects(id, name, reference, main_contractor, address, start_date, active)')
      .eq('user_id', profile.id)
      .then(({ data }) => {
        setProjects((data || []).map(pu => pu.projects).filter(Boolean));
        setLoading(false);
      });
  }, [profile]);

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <div style={{ background: '#000', padding: '20px 32px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <img src="/Proline Logo HQ.png" alt="Proline" style={{ height: '32px', width: 'auto' }} />
      </div>
      <div style={{ maxWidth: '720px', margin: '48px auto', padding: '0 24px' }}>
        <h1 style={{ fontSize: '20px', fontWeight: '500', color: C.text, marginBottom: '24px' }}>Select your project</h1>
        {loading ? (
          <div style={S.spinner}>Loading...</div>
        ) : projects.length === 0 ? (
          <div style={{ ...S.card, textAlign: 'center', padding: '48px', color: C.textMuted }}>
            You have not been assigned to any projects yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {projects.map(p => (
              <div
                key={p.id}
                onClick={() => navigate('project-detail', { projectId: p.id })}
                style={{ ...S.card, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', letterSpacing: '0.05em', marginBottom: '4px', textTransform: 'uppercase' }}>{p.reference}</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: C.text, marginBottom: '6px' }}>{p.name}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '13px', color: C.textMuted }}>
                      {p.main_contractor && <span><strong>MC:</strong> {p.main_contractor}</span>}
                      {p.start_date && <span><strong>Start:</strong> {formatDate(p.start_date)}</span>}
                      {p.address && <span>{p.address}</span>}
                    </div>
                  </div>
                  <span style={{ color: C.textLight, fontSize: '20px', flexShrink: 0, alignSelf: 'center' }}>›</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PendingApprovalScreen() {
  const { profile, signOut } = useAuth();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: C.bg, padding: '24px',
    }}>
      <div style={{ ...S.card, maxWidth: '480px', width: '100%', padding: '40px 32px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
        <h2 style={{ color: C.text, marginBottom: '8px', fontSize: '22px' }}>Account Pending Approval</h2>
        <p style={{ color: C.textMuted, fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
          Your account for <strong>{profile?.full_name}</strong> has been registered and is awaiting
          approval from an administrator. You will receive access once approved.
        </p>
        {profile?.company && (
          <p style={{ color: C.textMuted, fontSize: '13px', marginBottom: '24px' }}>
            Company: <strong>{profile.company}</strong>
          </p>
        )}
        <button onClick={signOut} style={{ ...S.btn, ...S.btnGhost }}>Sign Out</button>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { session, profile, loading, signOut, refreshProfile } = useAuth();
  const [nav, setNav] = useState({ page: 'dashboard', params: {} });
  // FIX 6: 'idle' → wait 5s → 'retrying' (auto-retry) → 'failed' (show error)
  const [profileRetryState, setProfileRetryState] = useState('idle');

  // Seed the initial history entry so the back button has somewhere to return to,
  // and listen for browser back/forward navigation.
  useEffect(() => {
    window.history.replaceState({ page: 'dashboard', params: {} }, '');

    function handlePopState(e) {
      const state = e.state || { page: 'dashboard', params: {} };
      setNav({ page: state.page || 'dashboard', params: state.params || {} });
      window.scrollTo(0, 0);
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // FIX 6: If loading finished, session exists, but profile is still null, wait 5s
  // then auto-retry fetchProfile once. Only show the error screen if the retry also
  // fails after 3s — two attempts before giving up.
  useEffect(() => {
    let mounted = true;
    if (session && !profile && !loading) {
      setProfileRetryState('idle');
      const retryTimer = setTimeout(async () => {
        if (!mounted) return;
        setProfileRetryState('retrying');
        const result = await Promise.race([
          refreshProfile(),
          new Promise(resolve => setTimeout(() => resolve(null), 3000)),
        ]);
        if (!mounted) return;
        if (!result) setProfileRetryState('failed');
        // If result is truthy, AuthContext has updated profile state and this
        // effect will re-run, resetting to 'idle' via the else branch below.
      }, 5000);
      return () => {
        mounted = false;
        clearTimeout(retryTimer);
      };
    }
    setProfileRetryState('idle');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, profile, loading]);

  // Non-admin roles get redirected away from dashboard on login
  useEffect(() => {
    const role = profile?.role;
    if (!profile || role === 'admin' || role === 'mc_qs' || nav.page !== 'dashboard') return;
    supabase
      .from('project_users')
      .select('project_id')
      .eq('user_id', profile.id)
      .then(({ data }) => {
        const ids = (data || []).map(r => r.project_id);
        if (ids.length === 1) {
          navigate('project-detail', { projectId: ids[0] });
        } else if (ids.length > 1) {
          navigate('project-selector', {});
        }
        // 0 projects: stay on dashboard (no project to go to)
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, profile?.role]);

  function navigate(page, params) {
    const state = { page, params: params || {} };
    window.history.pushState(state, '', window.location.pathname);
    setNav(state);
    window.scrollTo(0, 0);
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg }}>
        <div style={{ color: C.textMuted, fontSize: '16px' }}>Loading...</div>
      </div>
    );
  }

  if (!session) {
    if (nav.page === 'register') return <RegisterPage navigate={navigate} />;
    return <LoginPage navigate={navigate} />;
  }

  if (!profile) {
    if (profileRetryState === 'failed') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg, gap: '16px' }}>
          <div style={{ color: '#dc2626', fontSize: '16px', fontWeight: '600' }}>Session error — please log in again</div>
          <button onClick={signOut} style={{ padding: '10px 20px', background: '#000', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
            Sign In Again
          </button>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg }}>
        <div style={{ color: C.textMuted, fontSize: '16px' }}>
          {profileRetryState === 'retrying' ? 'Reconnecting...' : 'Loading profile...'}
        </div>
      </div>
    );
  }

  if (!profile.approved) {
    return <PendingApprovalScreen />;
  }

  const { page, params } = nav;

  function renderPage() {
    switch (page) {
      case 'dashboard':           return profile.role === 'admin' ? <Dashboard navigate={navigate} /> : profile.role === 'mc_qs' ? <MCPortalDashboard /> : <ProjectSelectorPage navigate={navigate} />;
      case 'project-selector':    return <ProjectSelectorPage navigate={navigate} />;
      case 'admin':               return profile.role === 'admin' ? <Admin navigate={navigate} params={params} /> : <ProjectSelectorPage navigate={navigate} />;
      case 'projects':            return <ProjectListPage navigate={navigate} />;
      case 'project-detail':      return <ProjectDetailPage navigate={navigate} params={params} />;
      case 'instruction-dayworks': return <InstructionDayworksPage navigate={navigate} params={params} />;
      case 'instruction-new':     return <InstructionForm navigate={navigate} params={params} />;
      case 'new-instruction':     return <InstructionForm navigate={navigate} params={params} />;
      case 'instruction-detail':  return <InstructionDetail navigate={navigate} params={params} />;
      case 'instructions':        return <ProjectListPage navigate={navigate} />;
      case 'daywork-new':         return <DayworkForm navigate={navigate} params={params} />;
      case 'new-daywork':         return <DayworkForm navigate={navigate} params={params} />;
      case 'daywork-edit':        return <DayworkForm navigate={navigate} params={params} />;
      case 'daywork-detail':      return <DayworkDetail navigate={navigate} params={params} />;
      case 'dayworks':            return <ProjectListPage navigate={navigate} />;
      case 'variation-new':       return <VariationForm navigate={navigate} params={params} />;
      case 'new-variation':       return <VariationForm navigate={navigate} params={params} />;
      case 'variation-edit':      return <VariationForm navigate={navigate} params={params} />;
      case 'variation-detail':    return <VariationDetail navigate={navigate} params={params} />;
      case 'variations':          return <AllVariationsPage navigate={navigate} />;
      default:                    return <Dashboard navigate={navigate} />;
    }
  }

  if (profile.role === 'mc_qs') {
    return renderPage();
  }

  return (
    <Layout navigate={navigate} currentPage={page}>
      {renderPage()}
    </Layout>
  );
}

export default function App() {
  const path = window.location.pathname;
  const token = new URLSearchParams(window.location.search).get('token');
  const dwMatch = path.match(/^\/mc-portal\/daywork\/([^/?]+)/);
  const varMatch = path.match(/^\/mc-portal\/variation\/([^/?]+)/);
  const ewnCviMatch = path.match(/^\/mc-portal\/ewn-cvi\/([^/?]+)/);
  if (dwMatch) return <MCPortalDaywork dayworkId={dwMatch[1]} token={token} />;
  if (varMatch) return <MCPortalVariation variationId={varMatch[1]} token={token} />;
  if (ewnCviMatch) return <MCPortalEwnCvi instructionId={ewnCviMatch[1]} token={token} />;
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
